/**
 * App-wide TanStack Query client.
 *
 * Lives in src/lib so App.tsx stays focused on routing + providers.
 *
 * Behaviour:
 *   - Stale time / cache time tuned for the CRM workload (queries are
 *     fresh for 5 min, cached for 30 min). Manual-refresh preferred over
 *     refetch-on-focus / refetch-on-reconnect. A screen refetches when it
 *     opens only if its data is stale, so a write on one page shows on
 *     every other page the next time it opens.
 *   - Cascade-friendly error logging — auth-hydration errors are
 *     suppressed from Sentry because they're already captured at source.
 *     Client transport failures (fetch never reached an edge function)
 *     are breadcrumbs, not Sentry exceptions (NYNCH-DW).
 *   - Background-refetch failures show a toast (queries that handle
 *     their own error UI opt out via meta.showErrorToast = false).
 *   - Mutation errors toast generically except for AuthenticationError /
 *     SessionExpiredError which AuthContext handles globally.
 */

import { QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AuthenticationError, SessionExpiredError } from '@/lib/auth-guard';
import { log } from '@/lib/logger';
import {
  extractErrorMessage,
  queryFailureLogLevel,
} from '@/lib/queryErrorReporting';

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      const errMsg = extractErrorMessage(error);
      const level = queryFailureLogLevel(error);
      const payload = {
        queryKey: JSON.stringify(query.queryKey),
        // Capture stack trace for hard-to-diagnose errors like "supabase is not defined"
        ...(errMsg.includes('is not defined') || errMsg.includes('unescaped latin') ? {
          stack: error instanceof Error ? error.stack : undefined,
        } : {}),
      };

      // log.error opens a Sentry issue. log.warn is a breadcrumb only.
      if (level === 'error') {
        log.error(`Query failed: ${errMsg}`, payload);
      } else if (level === 'warn') {
        log.warn(`Query failed: ${errMsg}`, payload);
      }

      // Show toast for queries that opt in (meta.showErrorToast !== false)
      // Most queries handle errors in their own UI, so this is a safety net
      if (query.meta?.showErrorToast !== false && query.state.data !== undefined) {
        // Only toast if there was previously data (i.e., a background refetch failed)
        // This avoids toasting on initial load failures which are shown inline
        toast.error(error instanceof Error ? error.message : 'Something went wrong');
      }
    },
  }),
  defaultOptions: {
    queries: {
      // Data is fresh for 5 minutes - won't refetch during this time
      staleTime: 5 * 60 * 1000,

      // Keep unused data in cache for 30 minutes
      gcTime: 30 * 60 * 1000,

      // Don't refetch on window focus for CRM data (manual refresh preferred)
      refetchOnWindowFocus: false,

      // Refetch on mount only when the data is stale: fresh data still opens
      // instantly on a tab switch, but a screen whose data was invalidated
      // while it was closed (a write on another page) loads the new data when
      // it opens. `false` here left every closed screen showing the old copy
      // until a full page refresh (Peter, 2026-11-03).
      refetchOnMount: true,

      // Don't refetch on reconnect (manual refresh preferred)
      refetchOnReconnect: false,

      // Keep showing previous data while fetching new data
      // This prevents loading flickers on tab switch
      placeholderData: (previousData: unknown) => previousData,

      // Retry logic
      retry: (failureCount, error) => {
        // Don't retry on auth errors
        if (error instanceof AuthenticationError || error instanceof SessionExpiredError) {
          return false;
        }
        // Retry up to 2 times with exponential backoff
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      // Retry mutations once
      retry: 1,
      onError: (error) => {
        // Handle auth errors globally
        // Note: When session expires, Supabase fires onAuthStateChange with user=null,
        // which the centralized AuthContext picks up. The page-level redirect logic
        // in Index.tsx then handles the redirect to /auth. No manual redirect needed here.
        if (error instanceof AuthenticationError || error instanceof SessionExpiredError) {
          toast.error(error.message);
          return;
        }

        // Generic error handling
        if (error instanceof Error) {
          toast.error(error.message || 'An error occurred');
        } else {
          toast.error('An unexpected error occurred');
        }
      },
    },
  },
});
